import styled, { Keyframes, css, keyframes } from 'styled-components';
import {
  paradigmLightBlue,
  razzmatazz,
} from '@paradigm/design-system/src/colors';

interface TimerProps {
  readonly duration: number; // seconds
  readonly remainder: number; // seconds
}

interface Props extends Omit<TimerProps, 'remainder'> {
  readonly elapsed?: number; // seconds
  readonly isColorAnimated?: boolean;
}

interface CircleProps extends Omit<Props, 'elapsed' | 'remainder'> {
  readonly animation?: Keyframes;
}

const dash = ({ remainder, duration }: TimerProps) => {
  const totalElapsed = Math.floor((remainder / duration) * 120);
  return keyframes`
  0% {
    stroke-dasharray: ${totalElapsed}, ${120 - totalElapsed};
  }
  100% {
    stroke-dasharray: 0, 130;
  }
`;
};

const dashWithColor = ({ remainder, duration }: TimerProps) => {
  const totalElapsed = Math.floor((remainder / duration) * 120);
  return keyframes`
  0% {
    stroke-dasharray: ${totalElapsed}, ${120 - totalElapsed};
    stroke: ${paradigmLightBlue};
  }
  40% {
    stroke: ${paradigmLightBlue};
  }
  100% {
    stroke-dasharray: 0, 130;
    stroke: ${razzmatazz};
  }
`;
};

export default function Timer({
  duration,
  elapsed = 0,
  isColorAnimated,
}: Props) {
  // If the elapsed time is higher that the duration we need to restart the timer.
  const remainderDuration =
    duration > elapsed
      ? Math.abs(duration - elapsed)
      : Math.abs(duration - (elapsed - duration));
  const animation =
    isColorAnimated === true
      ? dashWithColor({ remainder: remainderDuration, duration })
      : dash({ remainder: remainderDuration, duration });
  return (
    <Svg version="1.1" viewBox="0 0 50 50" data-testid="timer">
      <Background cx="25" cy="25" r="20" duration={0} />
      <Circle
        cx="25"
        cy="25"
        r="20"
        duration={remainderDuration}
        animation={animation}
      />
    </Svg>
  );
}

const Circle = styled.circle<CircleProps>`
  stroke-linecap: round;
  stroke-width: 10;
  opacity: 1;
  fill: none;
  stroke: ${paradigmLightBlue};

  ${(props) =>
    props.animation != null &&
    css`
      animation-name: ${props.animation};
      animation-duration: ${props.duration}s;
      animation-timing-function: linear;
      animation-iteration-count: infinite;
    `}
`;

const Background = styled(Circle)`
  stroke: transparent;
`;

const Svg = styled.svg`
  transform: rotate(90deg) scaleX(-1);
`;
